// 60 s TWAP of the twap60 settle method (ADR 095, spec D2-D4). The engine settles with the same algorithm; the
// page uses it to check a history file (to the cent) and for the live readout. Arithmetic is in integer cents.
import type { TickPoint } from './model';

/** The TWAP window: the 60 seconds [anchor-60, anchor). */
export const TWAP_WINDOW_S = 60;

const CENTS = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/;

/**
 * A price as integer cents, or undefined when it is not a positive price with at most 2 decimals.
 * It reads the shortest decimal string of the float (String), so 83652.01 is 8365201 cents.
 * It never truncates price * 100: that float can land just under the integer and lose one cent.
 */
export const toCents = (price: number): bigint | undefined => {
  if (typeof price !== 'number' || !(price > 0)) return undefined;
  const match = CENTS.exec(String(price));
  return match ? BigInt(match[1]) * 100n + BigInt((match[2] ?? '').padEnd(2, '0')) : undefined;
};

/**
 * TWAP in cents for `anchor` (D2-D4). The price of second s is the point at s. A missing second takes the price of
 * the second before it. A missing first second (anchor-60) takes the last point before the window.
 * TWAP = round half up (sum / 60) = (2 * sum + 60) / 120 in integers.
 * Undefined when no point is at or before anchor-60, when a price that the window uses is not a valid price with
 * at most 2 decimals, or when the anchor is not a whole second. `points`: ascending by second (parseTickFeed and
 * mergeTicks give this order).
 */
export const twapCents = (points: readonly TickPoint[], anchor: number): bigint | undefined => {
  if (!Number.isInteger(anchor)) return undefined;
  const start = anchor - TWAP_WINDOW_S;
  // The last point at or before the window start, searched from the tail: the work stays near 60 steps.
  let index = points.length - 1;
  while (index >= 0 && points[index][0] > start) index--;
  if (index < 0) return undefined;
  let cents = toCents(points[index][1]);
  let sum = 0n;
  for (let s = start; s < anchor; s++) {
    const before = index; // advance to the last point at or before second s
    while (index + 1 < points.length && points[index + 1][0] <= s) index++;
    if (index !== before) cents = toCents(points[index][1]);
    if (cents === undefined) return undefined;
    sum += cents;
  }
  return (2n * sum + 60n) / 120n;
};

/** The TWAP for `anchor` as a price with 2 decimals, or undefined (see twapCents). */
export const twapPrice = (points: readonly TickPoint[], anchor: number): number | undefined => {
  const cents = twapCents(points, anchor);
  return cents === undefined ? undefined : Number(cents) / 100;
};

/** The mean of the 60 seconds that end at the last point T: the TWAP for anchor T + 1 (D8). */
export const trailingTwap = (points: readonly TickPoint[]): number | undefined => {
  const last = points.at(-1);
  return last ? twapPrice(points, last[0] + 1) : undefined;
};
